import { Storage } from "aws-amplify";
import { S3ProviderListOutputItem } from "@aws-amplify/storage";
import { loggingWarn } from "../utils/LoggingUtils";
import JSZip from "jszip";
import awsmobile from "../aws-exports";
import { S3Client, DeleteObjectCommand, CopyObjectCommand } from "@aws-sdk/client-s3";

Storage.configure({
    customPrefix: {
        public: "",
    },
});

/**
 * ファイル一覧の取得
 * @param email メールアドレス
 * @returns
 */
export const getFileList = async (email: string) => {
    try {
        const files = await Storage.list(email + "/", {
            level: "public",
            pageSize: "ALL",
        });
        return files["results"];
    } catch (error) {
        loggingWarn("[S3 list] Failed:", error);
        throw error;
    }
};

/**
 * フォルダの作成
 * @param email メールアドレス
 * @param folderName フォルダ名（メールアドレスに続くプロジェクト名）
 */
export const createFolder = async (email: string, folderName: string | undefined) => {
    try {
        let key = email + "/";
        if (folderName) {
            const cleanedFolderName = folderName.replace(/\/+$/, "");
            key += cleanedFolderName + "/";
        }
        Storage.put(key, "");
    } catch (error) {
        loggingWarn("[S3 put] Failed:", error);
        throw error;
    }
};

/**
 * S3から複数ファイルをダウンロード（署名付きURLを使用）（2つ以上ならZIPにする）
 * @param keys ダウンロードするファイルのキー配列
 */
export const getFiles = async (keys: string[]) => {
    if (!keys?.length) return console.warn("[S3 get] No files specified.");

    // 単一ファイルの通常ダウンロード
    if (keys.length === 1) {
        const url = await Storage.get(keys[0], { expires: 60 });
        const a = document.createElement("a");
        a.href = url;
        a.download = keys[0].split("/").pop()!;
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
    }

    // 複数ファイルのZIP形式ダウンロード
    const zip = new JSZip();
    await Promise.all(
        keys.map(async (key) => {
            try {
                const url = await Storage.get(key, { expires: 60 });
                const blob = await (await fetch(url)).blob();
                zip.file(key.split("/").pop()!, blob);
            } catch (error) {
                console.warn(`[S3 get] Failed: ${key}`, error);
            }
        }),
    );
    const now = new Date();
    const dateStr = now.toISOString().replace(/[-T:]/g, "").slice(0, 13); // YYYYMMDD-HHmm
    const zipFilename = dateStr + ".zip";

    const zipBlob = await zip.generateAsync({ type: "blob" });
    const zipUrl = URL.createObjectURL(zipBlob);
    const a = document.createElement("a");
    a.href = zipUrl;
    a.download = zipFilename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(zipUrl);
};

/**
 * S3へファイルをアップロード
 * @param key
 * @param file
 * @returns
 */
export const putFile = async (key: string, file: File, onProgress?: (progress: number) => void) => {
    try {
        const result = await Storage.put(key, file, {
            contentType: file.type,
            progressCallback: (progress: { loaded: number; total: number }) => {
                if (onProgress) {
                    const percentage = Math.round((progress.loaded / progress.total) * 100);
                    console.log(
                        "putFile() : ",
                        percentage,
                        "%, Uploaded: ",
                        progress.loaded,
                        ", Total: ",
                        progress.total,
                    );
                    onProgress(percentage);
                }
            },
        });
        return result;
    } catch (error) {
        loggingWarn("[S3 put] Failed:", error);
        throw error;
    }
};

/**
 * S3からファイルを削除
 * @param {*} key
 * @returns
 */
export const removeFile = async (key: string) => {
    try {
        await Storage.remove(key);
    } catch (error) {
        loggingWarn("[S3 remove] Failed:", error);
        throw error;
    }
};

/**
 * S3からファイルを削除 [S3Client使用]
 * @param {*} key
 * @returns
 */
export const removeFileByS3Client = async (key: string, credentials: any) => {
    const s3Client = new S3Client({
        region: awsmobile.aws_user_files_s3_bucket_region,
        credentials,
    });
    const command = new DeleteObjectCommand({
        Bucket: awsmobile.aws_user_files_s3_bucket,
        Key: key,
    });
    await s3Client.send(command);
};

/**
 * ファイルのコピー [S3Client使用]
 */
export async function copyFileByS3Client(fromPath: string, toPath: string, credentials: any) {
    const s3Client = new S3Client({
        region: awsmobile.aws_user_files_s3_bucket_region,
        credentials,
    });
    const copyParams = {
        CopySource: `${awsmobile.aws_user_files_s3_bucket}/${encodeURIComponent(fromPath)}`,
        Bucket: awsmobile.aws_user_files_s3_bucket,
        Key: toPath,
    };
    await s3Client.send(new CopyObjectCommand(copyParams));
}

/**
 * ファイルの移動 [S3Client使用]
 */
export async function moveFile(fromPath: string, toPath: string, credentials: any) {
    await copyFileByS3Client(fromPath, toPath, credentials);
    await removeFileByS3Client(fromPath, credentials);
}

/**
 * ファイルリスト取得結果からプロジェクト名一覧を抽出する。
 *
 * ※S3の対象バケットにて、
 * バケット名/[メールアドレス]/[プロジェクト名]/[ファイル名]
 * の構成でファイルが保存されているとする。
 * @param {*} fileList
 * @returns
 */
export const extractProjects = (fileList: S3ProviderListOutputItem[]) => {
    const projectList: string[] = [];
    fileList
        .filter((res) => res.key?.split("/")[1])
        .forEach((res) => {
            const projectName = res.key?.split("/")[1];
            if (projectName && !projectList.includes(projectName)) {
                projectList.push(projectName);
            }
        });
    return projectList;
};

/**
 * ファイルリスト取得結果から指定したプロジェクト配下のファイルを抽出する
 *
 * @param {*} fileList
 * @param {*} projectName
 * @returns
 */
export const extractFilesByProject = (fileList: S3ProviderListOutputItem[], projectName: string) => {
    const newList: S3ProviderListOutputItem[] = [];
    fileList
        .filter((res) => res.size && res.size > 0) // ファイルに限定
        .filter((res) => projectName == res.key?.split("/")[1]) // プロジェクト名で絞り込み
        .forEach((res) => {
            newList.push(res);
        });
    return newList;
};

/**
 * ファイルリスト取得結果から指定したプロジェクトに指定したファイルが存在するか確認
 *
 * @param {*} fileList
 * @param {*} projectName
 * @param {*} targetFileName
 * @returns
 */
export const ifExistInSpecifiedProject = (
    fileList: S3ProviderListOutputItem[],
    projectName: string,
    targetFileName: string | undefined,
) => {
    return fileList
        .filter((res) => res.size && res.size > 0) // ファイルに限定
        .filter((res) => projectName == res.key?.split("/")[1]) // プロジェクト名で絞り込み
        .map((res) => res.key?.split("/")[2]) // ファイル名を抽出
        .some((name) => name == targetFileName);
};
